/**
 * Frontend API Response Schemas
 *
 * These schemas validate the actual API response format.
 * They're derived from @vhire/contracts but adapted for frontend needs.
 */

import { z } from 'zod';
import { normalizePublicEmploymentType } from '@vhire/contracts/public-job-display';

// =============================================================================
// Job Schemas (matches gateway transformJob output)
// =============================================================================

/**
 * Job listing as returned by GET /api/jobs
 */
export const JobSchema = z.object({
  id: z.string(),
  // Public candidate payloads intentionally omit the internal ownership id.
  tenantId: z.string().optional(),
  tenantSlug: z.string().nullable().optional(),
  slug: z.string().optional(),
  title: z.string(),
  company: z.string().optional().default(''),  // May not exist in Neon DB
  clientName: z.string().nullable().optional(),  // Alternative company field
  description: z.string().nullable().optional().default(''),
  location: z.string().nullable().optional(),
  locationLabel: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  remoteOk: z.boolean(),
  // Accept legacy strings at the boundary, but never guess an unknown value.
  jobType: z.string().nullable().optional().transform(normalizePublicEmploymentType),
  salaryRange: z.string().optional(),
  salaryMin: z.number().nullable().optional(),
  salaryMax: z.number().nullable().optional(),
  salaryCurrency: z.string().nullable().optional(),
  salaryPeriod: z.enum(['HOUR', 'DAY', 'WEEK', 'MONTH', 'YEAR']).nullable().optional(),
  salaryScale: z.enum(['units', 'lakhs']).nullable().optional(),
  applicantCountries: z.array(z.string()).optional(),
  searchLocations: z.array(z.object({ city: z.string().nullable(), state: z.string().nullable(), country: z.string() })).optional(),
  fullyRemote: z.boolean().optional(),
  workMode: z.enum(['onsite', 'remote', 'hybrid']).nullable().optional(),
  department: z.string().nullable().optional(),
  skills: z.array(z.string()).default([]),
  experienceLevel: z.enum(['entry', 'mid', 'senior', 'executive']).nullable().optional(),
  experienceRange: z.string().nullable().optional(),
  companyLogo: z.string().nullable().optional(),
  companyLogoDark: z.string().nullable().optional(), // dark-theme variant; Zod strips unlisted keys
  postedDate: z.number().nullable().optional(),
  updatedAt: z.number().nullable().optional(),
  postedDays: z.number().nullable().optional(),
  featured: z.boolean().default(false),
  status: z.string().default('active'),  // Can be 'active', 'published', 'paused', 'closed'
  applyMethod: z.enum(['vhire_direct', 'external', 'redirect']).nullable().optional(),
  applyUrl: z.string().url().nullable().optional(),
});

export type JobResponse = z.infer<typeof JobSchema>;

/**
 * Pagination info
 */
export const PaginationSchema = z.object({
  total: z.number().nonnegative().nullable(),
  limit: z.number(),
  offset: z.number(),
  hasMore: z.boolean(),
});

export type PaginationResponse = z.infer<typeof PaginationSchema>;

/**
 * Jobs list response
 */
export const JobsListResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    jobs: z.array(JobSchema),
    pagination: PaginationSchema,
  }).optional(),
  error: z.string().optional(),
});

export type JobsListResponse = z.infer<typeof JobsListResponseSchema>;

// =============================================================================
// Candidate Schemas
// =============================================================================

/**
 * Candidate profile as returned by GET /api/candidates/me
 */
export const CandidateProfileSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  marketplaceConsent: z.boolean().optional(),
  fullName: z.string().optional(),
  mobile: z.string().optional(),
  currentLocation: z.string().optional(),
  currentLat: z.number().min(-90).max(90).optional(),
  currentLng: z.number().min(-180).max(180).optional(),
  currentTitle: z.string().optional(),
  currentCompany: z.string().optional(),
  totalExperience: z.number().optional(),
  professionalSummary: z.string().optional(),
  keySkills: z.array(z.string()).optional(),
  linkedinUrl: z.string().optional(),
  githubUrl: z.string().optional(),
  portfolioUrl: z.string().optional(),
  resumeR2Key: z.string().optional(),
  avatarUrl: z.string().optional(),
  isActive: z.boolean().default(true),
  emailVerified: z.boolean().default(false),
  openToWork: z.boolean().default(true),
  workPreferences: z.object({
    workType: z.array(z.string()),
    workMode: z.array(z.string()),
    expectedCtc: z.string(),
    noticePeriod: z.string(),
  }).optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
});

export type CandidateProfileResponse = z.infer<typeof CandidateProfileSchema>;

// =============================================================================
// Application Schemas
// =============================================================================

/**
 * Application summary as returned by GET /api/candidates/applications
 */
export const ApplicationSummarySchema = z.object({
  id: z.string(),
  jobId: z.string(),
  candidateId: z.string().optional(), // May not always be present
  tenantId: z.string(),
  interviewId: z.string().optional(),
  // Status from backend may be 'new', 'submitted', etc. - be permissive
  status: z.string(),
  interviewScore: z.number().optional(),
  submittedAt: z.number().optional(), // Made optional - backend might use appliedAt instead
  updatedAt: z.number().optional(), // Made optional
  // Extended fields
  jobTitle: z.string().optional(),
  company: z.string().optional(),
  location: z.string().optional(),
  matchScore: z.number().optional(),
  interviewDate: z.number().optional(),
  salary: z.string().optional(),
  appliedAt: z.union([z.number(), z.string()]).optional(), // Can be number or ISO string
});

export type ApplicationSummaryResponse = z.infer<typeof ApplicationSummarySchema>;

// =============================================================================
// Tenant Schemas
// =============================================================================

/**
 * Tenant config as returned by GET /api/tenants/config
 */
export const TenantConfigSchema = z.object({
  tenantId: z.string(),
  slug: z.string(),
  name: z.string(),
  domain: z.string().optional(),
  branding: z.object({
    primaryColor: z.string().optional(),
    secondaryColor: z.string().optional(),
    logo: z.string().optional(),
    favicon: z.string().optional(),
  }).optional(),
  careerPage: z.object({
    heroTitle: z.string().optional(),
    heroSubtitle: z.string().optional(),
    showFilters: z.boolean().optional(),
    showSearch: z.boolean().optional(),
  }).optional(),
  social: z.record(z.string(), z.string()).optional(),
});

export type TenantConfigResponse = z.infer<typeof TenantConfigSchema>;

// =============================================================================
// Generic API Response Wrapper
// =============================================================================

/**
 * Standard API response wrapper
 */
export function createApiResponseSchema<T extends z.ZodTypeAny>(dataSchema: T) {
  return z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.string().optional(),
  });
}
